/* eslint-disable i18next/no-literal-string */
import React from 'react';
import { Link } from 'react-router';
import { cn } from '~/lib/utils';

export interface LogoProps {
  /**
   * Optional link to navigate to when logo is clicked
   */
  linkTo?: string;
  /**
   * Size variant of the logo
   */
  size?:
    | 'xs'
    | 'sm'
    | 'base'
    | 'lg'
    | 'xl'
    | '2xl'
    | '3xl'
    | '4xl'
    | '5xl'
    | '6xl';
  /**
   * Additional classes to apply to the logo
   */
  className?: string;
  /**
   * Whether to show the .io suffix
   */
  showSuffix?: boolean;
  /**
   * Optional click handler
   */
  onClick?: () => void;
}

/**
 * Logo component for the application
 */
export function Logo({
  linkTo,
  size = '6xl',
  className,
  showSuffix = true,
  onClick,
  ...props
}: LogoProps & Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'>) {
  const sizeClassMap = {
    xs: 'text-xs',
    sm: 'text-sm',
    base: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl',
    '2xl': 'text-2xl',
    '3xl': 'text-3xl',
    '4xl': 'text-4xl',
    '5xl': 'text-5xl',
    '6xl': 'text-6xl',
  };

  const suffixSizeMap = {
    xs: 'text-[0.5rem]',
    sm: 'text-[0.6rem]',
    base: 'text-[0.7rem]',
    lg: 'text-[0.8rem]',
    xl: 'text-xs',
    '2xl': 'text-xs',
    '3xl': 'text-sm',
    '4xl': 'text-sm',
    '5xl': 'text-base',
    '6xl': 'text-base',
  };

  const logoContent = (
    <div
      className={cn(
        sizeClassMap[size],
        'inline-block bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text leading-tight font-extrabold text-transparent',
        className
      )}
      style={{
        fontFamily: "'Poppins', sans-serif",
        textShadow:
          '0 0 20px rgba(0, 0, 0, 0.3), 0 0 40px rgba(96, 165, 250, 0.2)',
      }}
      onClick={onClick}
      {...props}
    >
      <span className="font-normal">imagi</span>
      name
      {showSuffix && <span className={suffixSizeMap[size]}>.io</span>}
    </div>
  );

  // If linkTo is provided, wrap in Link component
  if (linkTo) {
    return <Link to={linkTo}>{logoContent}</Link>;
  }

  return logoContent;
}
